import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { inkFieldChunk } from '../shaders/inkField.glsl.js';
import { getPaperTexture } from '../paper-texture.js';
import { Handwriting } from '../handwriting.js';

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'sumi',
  family: 'organic',
  energyLevel: 0.15,
  bpmMin: 40,
  bpmMax: 130,
  movement: 0.20,
  danceability: 0.30,
  tonality: 0.90,
  prefers: { dynamics: [0.45, 1.0], percussiveness: [0.0, 0.40], density: [0.0, 0.50] },
  enabled: true
};

// Sumi: black ink diffusing on paper, in the language of Japanese ink wash
// painting. The one visualizer in the library rendered as DARK PIGMENT ON
// LIGHT PAPER rather than additive light on black - a mostly-white frame is
// exactly what nothing else here can produce, and that contrast is the
// whole point. Never pulled toward the rest of the library's look.
//
// Consequence: NORMAL blending, depthWrite true, and every luminance in this
// file is kept under 0.74 (below the shared bloom pass's 0.78 threshold) -
// a paper background that bloomed would turn into a milky mess. The shared
// pipeline's film grain is left alone; it reads well here.
//
// Ink is a scalar density FIELD computed analytically from up to 12 DEPOSITS
// (a CPU-side ring buffer, uploaded as a flat vec4 uniform array each time
// one is added - never a per-frame upload), not particles - see
// stringVertexShader-style tier dispatch in resonance.js for the same
// "array size baked into the shader source at compile time" reason GLSL ES
// 1.0 forbids a variable-bound loop.
//
// Same rule as resonance.js/diorama.js: no director.pulse(), no beat pulse.
// Every deposit is born on a RISE of director.bands, nothing else.
export const TIER_LAYOUT = {
  0: { depositCount: 6, contourNoise: false },
  1: { depositCount: 8, contourNoise: true },
  2: { depositCount: 12, contourNoise: true }
};
const DEFAULT_TIER = 1;

// THE SIGNATURE. The wash is signed, bottom right, in the same ink: the
// track's name written out by a nib in a Hershey single-stroke hand. This was
// manuscript.js, a visualizer of its own that gave a whole page to the title;
// its paper and its blots were a weaker second copy of what this file already
// does, so the writing moved here as a component and the visualizer went away.
// See handwriting.js for the layout, the reveal and the font.
//
// Present on every tier - it is one draw call and a few hundred triangles, far
// cheaper than a deposit. Only the soft irregular EDGE follows the tier, the
// same trade-off contourNoise already makes for the ink field.
const SIGNATURE_RENDER_ORDER = 10;
// Deposits are kept off the words. A blot over a name reads as a mistake
// rather than as atmosphere - and late in a track _avgX has swept the deposits
// all the way to the right edge, which is precisely where the signature sits,
// so without this they would land on it every time.
const SIGNATURE_CLEARANCE_UV = 0.04;

const CAMERA_DIST = 6.0;
// Small margin only - this is a flat 2D wash meant to exactly fill the
// frame, not a parallax layer like diorama.js's photo plane. Just enough to
// absorb the shared Ken Burns drift (scene-manager.js) without exposing an
// edge; unlike diorama.js this file never rewrites camera.position itself,
// so there's no risk of drift accumulation to guard against - see that
// file's own header for why that specific fix was needed there and isn't
// here.
const PLANE_OVERFLOW_FACTOR = 1.15;

const RISE_THRESHOLD = 0.05;
const DEPOSIT_MIN_SPACING_S_BASE = 0.25; // spec's own plain baseline
const DEPOSIT_MIN_SPACING_S_SPARSE = 0.4; // character.density -> 0
const DEPOSIT_MIN_SPACING_S_DENSE = 0.2; // character.density -> 1

// Y bands in UV space (0 = bottom, 1 = top) - "position basse/centrale/
// haute dans le cadre".
const BAND_Y_RANGE = { low: [0.08, 0.32], mid: [0.36, 0.64], high: [0.68, 0.92] };
const BAND_FORCE = { low: 1.35, mid: 0.85, high: 0.5 };
const X_JITTER = 0.25;

const SPREAD = 0.20; // uSpread - shared growth-rate ceiling for every deposit

const DYNAMICS_MIN = 0.6;
const DYNAMICS_MAX = 1.3;

const DENSITY_GROWTH_MIN = 0.85; // progress=0
const DENSITY_GROWTH_MAX = 1.15; // progress=1

const TRACK_CLEAR_FADE_S = 2.5;

const STROKE_DRAW_MIN_S = 6;
const STROKE_DRAW_MAX_S = 12;
const STROKE_FADE_S = 3;
const STROKE_WIDTH_MAX_PX = 22;
const STROKE_WIDTH_MIN_PX = 3;
const STROKE_SAMPLES = 16;
const STROKE_FORCE = 1.1;

const INK_BASE_HEX = 0x14100c;
const INK_TINT_MIX = 0.25;
const PALETTE_LERP_MS = 900;

const PAPER_FALLBACK_COLOR = 0xf2ede2;

// "Reduis la densite d'encre de 60%" - inside the zone, ink is at 40% of
// its normal value. Same "own local chunk, own floor" pattern as
// resonance.js (0.30)/diorama.js (0.45) - reused here as a FRAGMENT-only
// function (a full-screen quad's own few vertices can't carry a
// vertex-interpolated attenuation value correctly; the safe zone is a
// localized box in the middle of the frame, not something a 4-corner
// bilinear lerp reconstructs) and multiplying `ink`, never the final
// colour, per spec.
const SUMI_SAFE_ZONE_MIN_ATTENUATION = 0.40;

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

const sumiVertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// count is baked in at shader-compile time - a fresh material is built per
// tier (see TIER_LAYOUT/QUALITY_TIER_AWARE_MODES), same convention as
// resonance.js's stringVertexShader(count).
function sumiFragmentShader(depositCount, hasContourNoise) {
  return `
    precision mediump float;

    uniform vec4 uDeposits[${depositCount}];
    uniform float uTime;
    uniform float uSpread;
    uniform float uOpacity;
    uniform float uDensityGrowth;
    uniform float uTrackChangeTime;
    uniform vec3 uInkColor;
    uniform sampler2D uPaper;
    uniform vec2 uResolution;
    uniform vec2 uStrokeP0;
    uniform vec2 uStrokeP1;
    uniform vec2 uStrokeP2;
    uniform vec2 uStrokeP3;
    uniform float uStrokeProgress;
    uniform float uStrokeOpacity;
    uniform float uStrokeSeed;
    uniform float uAspect;
    uniform vec2 uSafeCenter;
    uniform vec2 uSafeHalfSize;
    uniform float uSafeFeather;

    varying vec2 vUv;

    float safeZoneAttenuation(vec2 ndc) {
      vec2 s = vec2(ndc.x * uAspect, ndc.y);
      vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
      float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
      return mix(${SUMI_SAFE_ZONE_MIN_ATTENUATION.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
    }

    // Ink field (value noise + the gaussian-core-plus-drying-rim profile)
    // lives in shaders/inkField.glsl.js. It was extracted when manuscript.js
    // needed the same field; that file is gone and sumi.js is the only caller
    // left, but the chunk stays where it is - it is a self-contained piece of
    // GLSL with its own reasoning, and folding it back in would only make this
    // shader longer.
    ${inkFieldChunk(hasContourNoise)}

    vec2 bezierPoint(float t, vec2 p0, vec2 p1, vec2 p2, vec2 p3) {
      float mt = 1.0 - t;
      return mt * mt * mt * p0 + 3.0 * mt * mt * t * p1 + 3.0 * mt * t * t * p2 + t * t * t * p3;
    }

    float strokeInk(vec2 p) {
      if (uStrokeOpacity <= 0.001) return 0.0;
      float minD = 1.0e6;
      float minT = 0.0;
      vec2 prev = uStrokeP0;
      for (int i = 1; i <= ${STROKE_SAMPLES}; i++) {
        float t = float(i) / float(${STROKE_SAMPLES});
        if (t > uStrokeProgress + (1.0 / float(${STROKE_SAMPLES}))) break;
        vec2 cur = bezierPoint(t, uStrokeP0, uStrokeP1, uStrokeP2, uStrokeP3);
        vec2 pa = p - prev;
        vec2 ba = cur - prev;
        float h = clamp(dot(pa, ba) / max(dot(ba, ba), 1.0e-6), 0.0, 1.0);
        float d = length(pa - ba * h);
        if (d < minD) {
          minD = d;
          minT = mix(t - 1.0 / float(${STROKE_SAMPLES}), t, h);
        }
        prev = cur;
      }
      // Progressive reveal - a soft leading edge rather than a hard cutoff
      // at uStrokeProgress, so the tip doesn't pop frame to frame.
      float reveal = 1.0 - smoothstep(uStrokeProgress - 0.03, uStrokeProgress + 0.01, minT);
      // Thick at the start, tapering to thin at the end - "comme un pinceau
      // qui se vide".
      float widthPx = mix(${STROKE_WIDTH_MAX_PX.toFixed(1)}, ${STROKE_WIDTH_MIN_PX.toFixed(1)}, minT);
      float halfWidth = max((widthPx * 0.5) / uResolution.y, 1.0e-4);
      float sd = minD / halfWidth;
      return inkProfile(sd, p, uStrokeP0, uStrokeSeed, ${STROKE_FORCE.toFixed(2)}) * reveal * uStrokeOpacity;
    }

    void main() {
      vec2 p = vUv;
      float ink = 0.0;
      for (int i = 0; i < ${depositCount}; i++) {
        vec4 dep = uDeposits[i];
        float age = uTime - dep.z;
        if (age < 0.0) continue;
        float spread = uSpread * (1.0 - exp(-age * 0.55));
        float d = distance(p, dep.xy) / max(spread, 1.0e-3);
        float force = dep.w;
        // Deposits from BEFORE the current track's own start fade out over
        // TRACK_CLEAR_FADE_S while the new track's own deposits (born
        // after uTrackChangeTime) render at full, unfaded strength - "la
        // feuille se vide comme si l'encre sechait" while new ink already
        // appears normally, not both fading together.
        if (dep.z < uTrackChangeTime) {
          float clearT = clamp((uTime - uTrackChangeTime) / ${TRACK_CLEAR_FADE_S.toFixed(2)}, 0.0, 1.0);
          force *= (1.0 - clearT);
        }
        float blot = inkProfile(d, p, dep.xy, dep.z, force);
        blot *= exp(-age * 0.11);
        ink += blot;
      }
      ink += strokeInk(p);
      ink *= uDensityGrowth;

      vec2 ndc = (vUv * 2.0 - 1.0) * ${PLANE_OVERFLOW_FACTOR.toFixed(3)};
      ink *= safeZoneAttenuation(ndc);

      float a = 1.0 - exp(-ink * uOpacity);
      // Decode the paper texture's stored sRGB bytes (and uInkColor,
      // itself a plain sRGB swatch value) to linear before mixing -
      // renderer.outputColorSpace = SRGBColorSpace re-encodes whatever
      // this shader outputs on the assumption it's linear scene light.
      // Skipping the decode double-encodes an already-bright sRGB byte,
      // pushing it toward white - measured directly: the paper's own
      // ~0.92 sRGB value rendered at ~0.99, past the 0.78 bloom threshold
      // with zero ink on the page. toneMapped:false above only skips the
      // ACES curve; it does nothing about this second, separate cause.
      vec3 paperSrgb = texture2D(uPaper, vUv).rgb;
      vec3 paper = pow(paperSrgb, vec3(2.2));
      vec3 inkLinear = pow(uInkColor, vec3(2.2));
      vec3 col = mix(paper, inkLinear, clamp(a, 0.0, 1.0));

      // A correctly-decoded paper is still a genuinely light surface by
      // design (~0.83 linear here) - AC1's 0.74 ceiling is a hard cap on
      // top of that, not just a colour-pipeline correctness fix, so it
      // needs its own explicit clamp. Scaling the whole colour (not
      // per-channel) preserves hue/tint exactly the way laser-
      // cathedral.js's own clampLuminance does for its very different
      // (bright-on-black) reason.
      //
      // The threshold here is LINEAR, but AC1's "0.74" is measured off a
      // screenshot - i.e. the DISPLAYED, sRGB-encoded value - and sRGB
      // encoding brightens midtones relative to linear (its OETF is
      // steeper than a flat line below 1). The analytic inverse
      // (((0.74+0.055)/1.055)^2.4 =~ 0.507) still measured ~0.77-0.80 on
      // an actual capture, not 0.74 - close but not exact, likely a small
      // mismatch between this approximation and the real transfer
      // function/PNG encode path. Tuned down empirically from there
      // against real captures (0.44 -> 0.769, 0.37 -> 0.727 measured,
      // this last one with real margin under the ceiling) rather than
      // trusted analytically.
      float lum = dot(col, vec3(0.299, 0.587, 0.114));
      const float LUM_CAP_LINEAR = 0.37;
      if (lum > LUM_CAP_LINEAR) col *= LUM_CAP_LINEAR / max(lum, 0.001);

      gl_FragColor = vec4(col, 1.0);
    }
  `;
}

// Fallback so a missing paper texture still renders correctly - see
// paper-texture.js's own header. 1x1, same colour the spec names.
let _fallbackPaperTexture = null;
function fallbackPaperTexture() {
  if (!_fallbackPaperTexture) {
    const c = new THREE.Color(PAPER_FALLBACK_COLOR);
    _fallbackPaperTexture = new THREE.DataTexture(
      new Uint8Array([Math.round(c.r * 255), Math.round(c.g * 255), Math.round(c.b * 255), 255]), 1, 1, THREE.RGBAFormat
    );
    _fallbackPaperTexture.needsUpdate = true;
  }
  return _fallbackPaperTexture;
}

function randomStrokeControlPoints() {
  const y0 = 0.15 + Math.random() * 0.7;
  const y3 = 0.15 + Math.random() * 0.7;
  const midY = (y0 + y3) / 2 + (Math.random() * 2 - 1) * 0.3;
  return [
    new THREE.Vector2(-0.08, y0),
    new THREE.Vector2(0.25 + (Math.random() * 2 - 1) * 0.1, midY + (Math.random() * 2 - 1) * 0.2),
    new THREE.Vector2(0.75 + (Math.random() * 2 - 1) * 0.1, midY + (Math.random() * 2 - 1) * 0.2),
    new THREE.Vector2(1.08, y3)
  ];
}

export class SumiVisualizer extends Visualizer {
  constructor(options) {
    super();
    const params = new URLSearchParams(typeof location !== 'undefined' ? location.search : '');
    // ?sumitier=0|1|2 - forces a tier regardless of the governor's own
    // pick, for checking each tier's own look (in particular tier 0's
    // "pas de bruit sur le contour" trade-off) without waiting for a slow
    // device to actually get downgraded to it.
    const forcedTier = parseInt(params.get('sumitier'), 10);
    const tier = Number.isFinite(forcedTier) && TIER_LAYOUT[forcedTier] != null
      ? forcedTier
      : (options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER);
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;
    this._paperTexture = options && options.paperTexture !== undefined ? options.paperTexture : getPaperTexture();
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(0, 0, CAMERA_DIST);
    camera.lookAt(0, 0, 0);

    const layout = TIER_LAYOUT[this._tier];
    this._depositCount = layout.depositCount;

    const fovRad = THREE.MathUtils.degToRad(camera.fov);
    const frustumHeight = 2 * CAMERA_DIST * Math.tan(fovRad / 2);
    const frustumWidth = frustumHeight * camera.aspect;
    const planeWidth = frustumWidth * PLANE_OVERFLOW_FACTOR;
    const planeHeight = frustumHeight * PLANE_OVERFLOW_FACTOR;

    const geometry = new THREE.PlaneGeometry(planeWidth, planeHeight);
    // Pre-allocated once, mutated in place via .set() - an array of
    // THREE.Vector4, the well-supported shape for a `vec4[]` uniform
    // (THREE's flatten helper expects vector-like elements with their own
    // components, not a flat typed array of raw numbers - unlike a plain
    // `float[]` uniform, which resonance.js's uAmp/uPhase already use
    // safely as flat Float32Arrays).
    this._depositData = Array.from({ length: this._depositCount }, () => new THREE.Vector4(0, 0, 0, 0));
    this._writeIndex = 0;

    this._uniforms = Object.assign({}, safeZoneUniforms, {
      uDeposits: { value: this._depositData },
      uTime: { value: 0 },
      uSpread: { value: SPREAD },
      uOpacity: { value: 1 },
      uDensityGrowth: { value: DENSITY_GROWTH_MIN },
      uTrackChangeTime: { value: 0 },
      uInkColor: { value: new THREE.Color(INK_BASE_HEX) },
      uPaper: { value: this._paperTexture || fallbackPaperTexture() },
      uResolution: { value: new THREE.Vector2(1280, 720) },
      uStrokeP0: { value: new THREE.Vector2() },
      uStrokeP1: { value: new THREE.Vector2() },
      uStrokeP2: { value: new THREE.Vector2() },
      uStrokeP3: { value: new THREE.Vector2() },
      uStrokeProgress: { value: 0 },
      uStrokeOpacity: { value: 0 },
      uStrokeSeed: { value: Math.random() * 10 }
    });

    this._material = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: sumiVertexShader,
      fragmentShader: sumiFragmentShader(this._depositCount, layout.contourNoise),
      transparent: false,
      depthWrite: true,
      depthTest: true,
      side: THREE.DoubleSide,
      // NORMAL blending (THREE's default) - deliberately not additive, see
      // this file's own header.
      //
      // toneMapped:false is load-bearing for AC1, not defensive. scene-
      // manager.js runs ACESFilmicToneMapping, and it does NOT just gently
      // compress bright values the way "filmic" suggests - measured
      // directly (raw canvas pixels, paper texture alone, no ink): the
      // paper's own ~0.92 sRGB byte value came out the OTHER side at
      // ~0.99, past the 0.78 bloom threshold before a single drop of ink
      // is on the page. Same root cause as resonance.js's background bug
      // (a raw sRGB byte fed through a curve that assumes linear scene
      // radiance behaves unpredictably, not simply "dimmer" or "brighter"
      // in an obvious direction) - here it happens to boost rather than
      // crush. Bypassing the curve makes this file's own output values
      // the ONLY thing that determines the final luminance, which is what
      // lets PAPER_FALLBACK_COLOR/uInkColor actually hit a precise,
      // verifiable ceiling instead of an ACES-dependent guess.
      toneMapped: false
    });
    this._mesh = new THREE.Mesh(geometry, this._material);
    this._scene.add(this._mesh);

    this._handwriting = new Handwriting({
      edgeNoise: layout.contourNoise,
      renderOrder: SIGNATURE_RENDER_ORDER
    });
    this._handwriting.init(scene, camera);

    this._time = 0;
    this._lastDepositTime = -1e6;
    this._prevBandLow = 0;
    this._prevBandMid = 0;
    this._prevBandHigh = 0;
    this._nextRandomDepositAt = 0;
    this._avgX = 0;

    this._lastTrackId = undefined;
    this._trackChangeTime = 0;

    this._lastPaletteRef = undefined;
    this._inkColorFrom = new THREE.Color(INK_BASE_HEX);
    this._inkColorTo = new THREE.Color(INK_BASE_HEX);
    this._paletteLerpStart = -Infinity;

    // Stroke state machine - see this file's own header ("LE TRAIT").
    this._strokeState = 'drawing';
    this._strokeControlPoints = randomStrokeControlPoints();
    this._strokeDrawStart = 0;
    this._strokeDrawDuration = STROKE_DRAW_MIN_S + Math.random() * (STROKE_DRAW_MAX_S - STROKE_DRAW_MIN_S);
    this._strokeFadeStart = 0;
    this._lastDirectorMode = null;
    this._applyStrokeControlPoints();
  }

  _applyStrokeControlPoints() {
    const [p0, p1, p2, p3] = this._strokeControlPoints;
    this._uniforms.uStrokeP0.value.copy(p0);
    this._uniforms.uStrokeP1.value.copy(p1);
    this._uniforms.uStrokeP2.value.copy(p2);
    this._uniforms.uStrokeP3.value.copy(p3);
    this._uniforms.uStrokeSeed.value = Math.random() * 10;
  }

  // The signature's own box in this file's DEPOSIT coordinates, which are the
  // plane's raw uv - not the height units handwriting.js lays out in, and not
  // ndc either. The plane overflows the frustum by PLANE_OVERFLOW_FACTOR, so
  // that factor is part of the conversion: a height-unit x maps to
  // x / (aspect * overflow) + 0.5, and y likewise without the aspect.
  _signatureBoxUv() {
    const b = this._handwriting && this._handwriting.bounds;
    if (!b) return null;
    const k = PLANE_OVERFLOW_FACTOR;
    const a = this._camera.aspect || 1;
    return {
      u0: b.x0 / (a * k) + 0.5 - SIGNATURE_CLEARANCE_UV,
      u1: b.x1 / (a * k) + 0.5 + SIGNATURE_CLEARANCE_UV,
      v0: b.y0 / k + 0.5 - SIGNATURE_CLEARANCE_UV,
      v1: b.y1 / k + 0.5 + SIGNATURE_CLEARANCE_UV
    };
  }

  // A new deposit is a write into the ring buffer - O(1), no allocation.
  _addDeposit(bandKey, force) {
    const yRange = BAND_Y_RANGE[bandKey];
    let y = yRange[0] + Math.random() * (yRange[1] - yRange[0]);
    let x = clamp01(this._avgX + (Math.random() * 2 - 1) * X_JITTER);
    // Rejection sampling, bounded: a handful of tries, then the deposit lands
    // wherever it landed. Giving up beats skipping it - a missed deposit is a
    // beat the wash did not answer, which is more visible than a blot that
    // grazes a letter, and the low band's box can genuinely overlap the
    // signature enough that no sample is ever free.
    const box = this._signatureBoxUv();
    if (box) {
      for (let tries = 0; tries < 8; tries++) {
        if (x < box.u0 || x > box.u1 || y < box.v0 || y > box.v1) break;
        y = yRange[0] + Math.random() * (yRange[1] - yRange[0]);
        x = clamp01(this._avgX + (Math.random() * 2 - 1) * X_JITTER);
      }
    }
    const idx = this._writeIndex;
    this._depositData[idx].set(x, y, this._time, force);
    this._writeIndex = (this._writeIndex + 1) % this._depositCount;
    this._lastDepositTime = this._time;
  }

  _minSpacingS(profile) {
    const character = profile.director && profile.director.character;
    const density = character ? character.density : null;
    if (density == null) return DEPOSIT_MIN_SPACING_S_BASE;
    return lerp(DEPOSIT_MIN_SPACING_S_SPARSE, DEPOSIT_MIN_SPACING_S_DENSE, density);
  }

  // "Un depot nait sur une MONTEE d'enveloppe" - only the rising edge of a
  // band excites a new deposit, same "rise, not level" rule as
  // resonance.js's string excitation. If more than one band rises past
  // threshold in the same frame, only the strongest one deposits - one
  // deposit per spacing window, never a burst.
  _maybeDeposit(profile, dynAmount) {
    // Rise-tracking runs unconditionally, every frame, BEFORE the spacing
    // gate - the gate only decides whether a detected rise is allowed to
    // actually create a deposit. Gating this whole block (an earlier
    // version did) leaves _prevBand* stale for however long the gate
    // stays shut (200-400ms, several frames): the comparison the next
    // time it opens is then against a value from well before, producing a
    // spuriously inflated (or missed) rise instead of "this rise happened
    // while gated, so no deposit" - which is what the spacing rule is
    // actually supposed to mean.
    const bands = profile.director ? profile.director.bands : null;
    const low = bands ? bands.low : 0;
    const mid = bands ? bands.mid : 0;
    const high = bands ? bands.high : 0;
    const riseLow = Math.max(0, low - this._prevBandLow);
    const riseMid = Math.max(0, mid - this._prevBandMid);
    const riseHigh = Math.max(0, high - this._prevBandHigh);
    this._prevBandLow = low;
    this._prevBandMid = mid;
    this._prevBandHigh = high;

    if (this._time - this._lastDepositTime < this._minSpacingS(profile)) return;

    let bandKey = null;
    let bestRise = RISE_THRESHOLD;
    if (riseLow >= bestRise) { bandKey = 'low'; bestRise = riseLow; }
    if (riseMid >= bestRise) { bandKey = 'mid'; bestRise = riseMid; }
    if (riseHigh >= bestRise) { bandKey = 'high'; bestRise = riseHigh; }
    if (!bandKey) return;

    const force = BAND_FORCE[bandKey] * dynAmount * lerp(0.7, 1.3, Math.min(1, bestRise / 0.3));
    this._addDeposit(bandKey, force);
  }

  // "Sans bands: depots aleatoires toutes les 500 a 1500 ms" - same
  // repertoire-appropriate fallback as resonance.js's own no-grid pluck:
  // this music has no reliable pulse to fall back TO either.
  _maybeRandomDeposit(dynAmount) {
    if (this._time < this._nextRandomDepositAt) return;
    const keys = ['low', 'mid', 'high'];
    const bandKey = keys[Math.floor(Math.random() * keys.length)];
    const force = BAND_FORCE[bandKey] * dynAmount * (0.8 + Math.random() * 0.4);
    this._addDeposit(bandKey, force);
    this._nextRandomDepositAt = this._time + 0.5 + Math.random() * 1.0;
  }

  _updateStroke(profile) {
    const mode = profile.director ? profile.director.mode : null;
    // "redessinee a chaque frontiere de segment de la structure (jamais a
    // chaque phrase)" - director.mode IS the structure segment name
    // (intro/build/drop/... - see director.js), so a change in its value
    // is exactly a segment boundary. Only fires while HOLDING - a boundary
    // arriving mid-draw or mid-fade is ignored rather than restarting the
    // state machine on top of itself.
    if (mode != null && mode !== this._lastDirectorMode) {
      this._lastDirectorMode = mode;
      if (this._strokeState === 'holding') {
        this._strokeState = 'fading';
        this._strokeFadeStart = this._time;
      }
    } else if (this._lastDirectorMode == null) {
      this._lastDirectorMode = mode;
    }

    if (this._strokeState === 'drawing') {
      const t = clamp01((this._time - this._strokeDrawStart) / this._strokeDrawDuration);
      this._uniforms.uStrokeProgress.value = t;
      this._uniforms.uStrokeOpacity.value = 1;
      if (t >= 1) this._strokeState = 'holding';
    } else if (this._strokeState === 'holding') {
      this._uniforms.uStrokeProgress.value = 1;
      this._uniforms.uStrokeOpacity.value = 1;
    } else if (this._strokeState === 'fading') {
      const t = clamp01((this._time - this._strokeFadeStart) / STROKE_FADE_S);
      this._uniforms.uStrokeOpacity.value = 1 - t;
      if (t >= 1) {
        this._strokeControlPoints = randomStrokeControlPoints();
        this._applyStrokeControlPoints();
        this._strokeState = 'drawing';
        this._strokeDrawStart = this._time;
        this._strokeDrawDuration = STROKE_DRAW_MIN_S + Math.random() * (STROKE_DRAW_MAX_S - STROKE_DRAW_MIN_S);
        this._uniforms.uStrokeProgress.value = 0;
        this._uniforms.uStrokeOpacity.value = 1;
      }
    }
  }

  // AdaptivePartyVisualizer never forwards onTrackChange to its active
  // sub-visualizer (see resonance.js/diorama.js's own identical finding),
  // so palette AND scene-identity react by polling instead.
  _maybeBeginPaletteTransition(profile) {
    if (profile.artworkPalette === this._lastPaletteRef) return;
    this._lastPaletteRef = profile.artworkPalette;
    const palette = profile.artworkPalette;
    const dominant = palette && palette.dominantColors;
    const target = new THREE.Color(INK_BASE_HEX);
    if (dominant && dominant.length > 0) {
      target.lerp(new THREE.Color(dominant[0]), INK_TINT_MIX);
    }
    const t = clamp01((performance.now() - this._paletteLerpStart) / PALETTE_LERP_MS);
    // Snapshot wherever the lerp currently sits as the new FROM, so a
    // palette change mid-transition doesn't jump.
    this._inkColorFrom.lerp(this._inkColorTo, t);
    this._inkColorTo = target;
    this._paletteLerpStart = performance.now();
  }

  _updatePaletteLerp() {
    const t = clamp01((performance.now() - this._paletteLerpStart) / PALETTE_LERP_MS);
    this._uniforms.uInkColor.value.copy(this._inkColorFrom).lerp(this._inkColorTo, t);
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';

    if (profile.trackId !== this._lastTrackId) {
      this._lastTrackId = profile.trackId;
      if (this._lastTrackId !== undefined) {
        this._trackChangeTime = this._time;
      }
    }
    this._maybeBeginPaletteTransition(profile);
    this._updatePaletteLerp();

    if (!paused) {
      this._time += deltaTime;

      const character = profile.director && profile.director.character;
      const dynamics = character ? character.dynamics : null;
      const dynAmount = dynamics != null ? lerp(DYNAMICS_MIN, DYNAMICS_MAX, dynamics) : 1.0;

      const hasGrid = !!(profile.director && profile.director.hasReliableGrid);
      if (hasGrid) {
        this._maybeDeposit(profile, dynAmount);
      } else {
        this._maybeRandomDeposit(dynAmount);
      }

      // "la position horizontale progresse lentement de gauche a droite au
      // fil du morceau" - driven directly by track progress, not its own
      // separate clock, so it naturally reaches the right edge exactly as
      // the track ends regardless of how many deposits actually fired.
      const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
      this._avgX = progress;

      this._updateStroke(profile);

      this._uniforms.uOpacity.value = dynAmount;
      this._uniforms.uDensityGrowth.value = lerp(DENSITY_GROWTH_MIN, DENSITY_GROWTH_MAX, progress);
    }
    // paused: diffusion (deposit ages are uTime-relative and uTime itself
    // is frozen below) and the stroke's own state machine both hold -
    // "gel de la diffusion et du trace".

    // The signature is written in the wash's OWN ink, tint and all, so the two
    // never drift apart on a track whose cover pulls the palette somewhere
    // unexpected. It runs unpaused-gated on its own (handwriting.js freezes its
    // nib when playbackState is not PLAYING) rather than inside the !paused
    // block here, so a track change while paused still gets picked up.
    this._handwriting.setInkColor(this._uniforms.uInkColor.value);
    this._handwriting.update(deltaTime, profile);

    this._uniforms.uTime.value = this._time;
    this._uniforms.uTrackChangeTime.value = this._trackChangeTime;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this._uniforms.uResolution.value.set(window.innerWidth * dpr, window.innerHeight * dpr);
  }

  dispose() {
    this._handwriting.dispose();
    this._scene.remove(this._mesh);
    this._mesh.geometry.dispose();
    this._material.dispose();
    // uPaper is a shared, preloaded asset (see paper-texture.js) and must
    // NOT be disposed here; the fallback texture is a shared module-level
    // singleton too.
  }
}
